import React, { useEffect, useState } from "react";
import { Cake, Utensils, Play, RotateCcw, Activity, Film } from "lucide-react";
import { getEventState, setEventState } from "../lib/api";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

const ACTIONS = [
  {
    id: "show_running",
    label: "Start The Show",
    icon: Play,
    color: "bg-emerald-600 hover:bg-emerald-700",
    description: "Opens castle doors → 22s anticipation intro (\"Who's coming to my party?\") → Sky-of-Stars / Memories live rotation.",
  },
  {
    id: "memories_show",
    label: "Play Anagha's Memories",
    icon: Film,
    color: "bg-indigo-600 hover:bg-indigo-700",
    description: "Switches the projector to a continuous 3D slideshow of Anagha's memory photos. Great for the dinner interval or a quiet moment.",
  },
  {
    id: "cake_prep",
    label: "Start Cake Cutting",
    icon: Cake,
    color: "bg-rose-600 hover:bg-rose-700",
    description: "Pauses sky/memories rotation, plays buildup messages, then auto-advances to fireworks → dance floor.",
  },
  {
    id: "dinner",
    label: "Start Dinner",
    icon: Utensils,
    color: "bg-amber-700 hover:bg-amber-800",
    description: "Switches projector to a loop of Anagha's messages, guest photos, and memory pictures.",
  },
  {
    id: "auto",
    label: "Resume Auto Mode",
    icon: RotateCcw,
    color: "bg-stone-600 hover:bg-stone-700",
    description: "Hands control back to the time-based default flow (pre-event → sky/memories loop).",
  },
];

const STATUS_LABELS = {
  auto: { tone: "stone", text: "Auto (time-based)" },
  show_running: { tone: "emerald", text: "Show running (sky / memories rotation)" },
  memories_show: { tone: "indigo", text: "Anagha's Memories slideshow" },
  cake_prep: { tone: "rose", text: "Cake cutting buildup" },
  cake_celebrate: { tone: "rose", text: "Cake celebration fireworks" },
  dancing: { tone: "purple", text: "Dance floor" },
  dinner: { tone: "amber", text: "Dinner mode" },
  farewell: { tone: "indigo", text: "Final farewell" },
};

export default function DirectorControls() {
  const [state, setStateLocal] = useState({ phase: "auto", phase_started_at: null });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let alive = true;
    const load = () => getEventState().then((s) => { if (alive) setStateLocal(s); }).catch(() => {});
    load();
    const tm = setInterval(load, 3000);
    return () => { alive = false; clearInterval(tm); };
  }, []);

  const trigger = async (phase) => {
    const confirms = {
      show_running: "Start the show right now? The doors will open and Sky-of-Stars begins.",
      memories_show: "Play Anagha's memory slideshow now?",
      cake_prep: "Start cake cutting? Projector will switch immediately.",
      dinner: "Switch to dinner mode now?",
      auto: "Resume automatic time-based flow?",
    };
    if (!window.confirm(confirms[phase] || `Switch to ${phase}?`)) return;
    setBusy(true); setMsg("");
    try {
      const s = await setEventState(phase);
      setStateLocal(s);
      setMsg("Director updated.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS_LABELS[state.phase] || STATUS_LABELS.auto;
  const startedAgo = state.phase_started_at
    ? Math.floor((Date.now() - new Date(state.phase_started_at).getTime()) / 1000)
    : null;

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="director-controls">
      <div className="flex items-center gap-2 mb-1">
        <Play size={20} className="text-plum" />
        <h2 className="font-display text-xl text-ink">Director Controls</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-3xl">
        These buttons control what plays on the projector right now. The Director (
        <a href="/director" className="font-mono text-plum hover:underline">/director</a>)
        polls every 2 seconds — your guests will see the new scene within seconds.
      </p>

      {/* Live status */}
      <div
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 text-xs font-medium bg-${status.tone}-100 text-${status.tone}-800 border border-${status.tone}-200`}
        data-testid="director-status"
      >
        <Activity size={13} className="animate-pulse" />
        <span style={TE}>Currently: {status.text}</span>
        {startedAgo !== null && state.phase !== "auto" && (
          <span className="opacity-70">· {formatAgo(startedAgo)}</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const active =
            state.phase === a.id ||
            (a.id === "cake_prep" && ["cake_prep", "cake_celebrate", "dancing"].includes(state.phase));
          return (
            <button
              key={a.id}
              onClick={() => trigger(a.id)}
              disabled={busy || active}
              className={`${a.color} text-white rounded-2xl p-4 text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              data-testid={`director-action-${a.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} />
                <span className="font-semibold">{a.label}</span>
                {active && <span className="ml-auto text-[10px] bg-white/20 rounded-full px-2 py-0.5">Active</span>}
              </div>
              <p className="text-xs text-white/85 leading-snug">{a.description}</p>
            </button>
          );
        })}
      </div>

      {msg && (
        <p className="mt-3 text-xs text-plum" data-testid="director-msg">{msg}</p>
      )}
    </section>
  );
}

function formatAgo(seconds) {
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}
